import { useState, useEffect, useCallback } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Utensils, Plus, Edit, Trash2, Users, DollarSign, Calendar, Star, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const authHeader = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });
const STATUS_COLORS = { available: 'bg-emerald-100 text-emerald-700', booked: 'bg-red-100 text-red-700', maintenance: 'bg-gray-100 text-gray-600', inquiry: 'bg-purple-100 text-purple-700', confirmed: 'bg-blue-100 text-blue-700', cancelled: 'bg-red-100 text-red-700', done: 'bg-gray-100 text-gray-600' };
const STATUS_LABEL = { available: 'Disponible', booked: 'Reservado', maintenance: 'Mantenimiento', inquiry: 'Consulta', confirmed: 'Confirmado', cancelled: 'Cancelado', done: 'Completado' };
const EMPTY_ROOM = { name: '', capacity_min: 2, capacity_max: 20, price_per_person: 0, min_spend: 0, amenities: '', description: '', photos: [] };
const EMPTY_BOOKING = { room_id: '', room_name: '', client_name: '', client_email: '', client_phone: '', event_date: '', event_time: '20:00', guests: 10, occasion: '', menu_id: '', special_requests: '', budget: '' };

export default function PrivateDining() {
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [editRoom, setEditRoom] = useState(null);
  const [roomForm, setRoomForm] = useState(EMPTY_ROOM);
  const [bookingForm, setBookingForm] = useState(EMPTY_BOOKING);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, bRes, mRes] = await Promise.all([
        fetch(`${API_URL}/api/private-dining/rooms`, { headers: authHeader() }),
        fetch(`${API_URL}/api/private-dining/bookings`, { headers: authHeader() }),
        fetch(`${API_URL}/api/private-dining/menus`, { headers: authHeader() }),
      ]);
      if (rRes.ok) setRooms(await rRes.json());
      if (bRes.ok) setBookings(await bRes.json());
      if (mRes.ok) setMenus(await mRes.json());
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveRoom = async () => {
    if (!roomForm.name) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const method = editRoom ? 'PUT' : 'POST';
      const url = editRoom ? `${API_URL}/api/private-dining/rooms/${editRoom.id}` : `${API_URL}/api/private-dining/rooms`;
      const res = await fetch(url, { method, headers: authHeader(), body: JSON.stringify(roomForm) });
      if (res.ok) { toast.success(editRoom ? 'Sala actualizada' : 'Sala creada'); setShowRoomModal(false); load(); }
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const saveBooking = async () => {
    if (!bookingForm.room_id || !bookingForm.client_name || !bookingForm.event_date) { toast.error('Completa los campos requeridos'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/private-dining/bookings`, { method: 'POST', headers: authHeader(), body: JSON.stringify(bookingForm) });
      if (res.ok) { toast.success('Reservación privada creada'); setShowBookingModal(false); load(); }
    } catch { toast.error('Error'); }
    finally { setSaving(false); }
  };

  const updateBooking = async (id, updates) => {
    await fetch(`${API_URL}/api/private-dining/bookings/${id}`, { method: 'PUT', headers: authHeader(), body: JSON.stringify(updates) });
    toast.success('Actualizado'); load();
  };

  const deleteRoom = async (id) => {
    if (!window.confirm('¿Eliminar esta sala?')) return;
    await fetch(`${API_URL}/api/private-dining/rooms/${id}`, { method: 'DELETE', headers: authHeader() });
    toast.success('Sala eliminada'); load();
  };

  const OCCASIONS = ['Aniversario', 'Cumpleaños', 'Cena de negocios', 'Propuesta de matrimonio', 'Despedida de soltero(a)', 'Graduación', 'Reunión familiar', 'Celebración corporativa', 'Otro'];

  const stats = [
    { label: 'Salas', value: rooms.length, icon: Utensils, color: 'text-orange-500' },
    { label: 'Reservaciones activas', value: bookings.filter(b => ['inquiry', 'confirmed'].includes(b.status)).length, icon: Calendar, color: 'text-blue-500' },
    { label: 'Confirmadas', value: bookings.filter(b => b.status === 'confirmed').length, icon: CheckCircle, color: 'text-emerald-500' },
    { label: 'Total comensales', value: bookings.reduce((s, b) => s + (Number(b.guests) || 0), 0), icon: Users, color: 'text-purple-500' },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopBar title="Comedores Privados" subtitle="Gestiona salas privadas, menús especiales y reservaciones exclusivas" helpSection="private-dining" />
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => (
            <Card key={i}><CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-8 h-8 ${s.color}`} />
              <div><p className="text-2xl font-black">{s.value}</p><p className="text-xs text-gray-500">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </div>

        <Tabs defaultValue="rooms">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <TabsList>
              <TabsTrigger value="rooms">Salas</TabsTrigger>
              <TabsTrigger value="bookings">Reservaciones</TabsTrigger>
            </TabsList>
            <div className="flex gap-2">
              <Button onClick={() => { setEditRoom(null); setRoomForm(EMPTY_ROOM); setShowRoomModal(true); }} variant="outline" className="gap-1"><Plus className="w-4 h-4" /> Sala</Button>
              <Button onClick={() => setShowBookingModal(true)} className="bg-orange-500 hover:bg-orange-600 text-white gap-1"><Calendar className="w-4 h-4" /> Nueva Reservación</Button>
            </div>
          </div>

          <TabsContent value="rooms">
            {loading ? <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-orange-500 animate-spin" /></div>
            : rooms.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <Utensils className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No hay salas privadas configuradas</p>
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { setEditRoom(null); setRoomForm(EMPTY_ROOM); setShowRoomModal(true); }}><Plus className="w-4 h-4 mr-1" /> Crear primera sala</Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {rooms.map(room => (
                  <Card key={room.id} className="hover:shadow-md transition-shadow overflow-hidden">
                    <div className="h-3 bg-gradient-to-r from-orange-500 to-amber-400" />
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-black text-gray-900 text-lg">{room.name}</h3>
                        <Badge className={STATUS_COLORS[room.status] || STATUS_COLORS.available}>{STATUS_LABEL[room.status] || 'Disponible'}</Badge>
                      </div>
                      <p className="text-gray-500 text-sm mb-3">{room.description || 'Sala privada exclusiva'}</p>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <p className="font-bold text-gray-900">{room.capacity_min}–{room.capacity_max}</p>
                          <p className="text-xs text-gray-500">Personas</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-2 text-center">
                          <p className="font-bold text-gray-900">${(room.min_spend || 0).toLocaleString()}</p>
                          <p className="text-xs text-gray-500">Consumo mín.</p>
                        </div>
                      </div>
                      {room.amenities && <p className="text-xs text-gray-400 mb-3">✨ {room.amenities}</p>}
                      <div className="flex gap-2">
                        <Button size="sm" className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { setBookingForm({ ...EMPTY_BOOKING, room_id: room.id, room_name: room.name }); setShowBookingModal(true); }}>Reservar</Button>
                        <Button size="sm" variant="ghost" onClick={() => { setEditRoom(room); setRoomForm(room); setShowRoomModal(true); }}><Edit className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteRoom(room.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings">
            <div className="space-y-3">
              {bookings.length === 0 ? (
                <div className="text-center py-16 text-gray-400"><Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay reservaciones privadas</p></div>
              ) : bookings.map(b => (
                <Card key={b.id}><CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-gray-900">{b.client_name}</h4>
                        <Badge className={STATUS_COLORS[b.status]}>{STATUS_LABEL[b.status]}</Badge>
                        {b.occasion && <Badge variant="outline" className="text-xs">{b.occasion}</Badge>}
                      </div>
                      <p className="text-sm text-gray-500">{b.room_name} · {b.guests} personas · {b.event_date} {b.event_time}</p>
                      {b.special_requests && <p className="text-xs text-gray-400 mt-1">📝 {b.special_requests}</p>}
                    </div>
                    <div className="flex gap-2">
                      {b.status === 'inquiry' && (
                        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => updateBooking(b.id, { status: 'confirmed' })}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Confirmar
                        </Button>
                      )}
                      {['inquiry', 'confirmed'].includes(b.status) && (
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => updateBooking(b.id, { status: 'cancelled' })}>
                          <XCircle className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent></Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Room Modal */}
      <Dialog open={showRoomModal} onOpenChange={setShowRoomModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editRoom ? 'Editar Sala' : 'Nueva Sala Privada'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre de la sala *</Label><Input value={roomForm.name} onChange={e => setRoomForm(p => ({ ...p, name: e.target.value }))} placeholder="Sala Presidencial, The Wine Room..." /></div>
            <div><Label>Descripción</Label><Textarea value={roomForm.description} onChange={e => setRoomForm(p => ({ ...p, description: e.target.value }))} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Mín. personas</Label><Input type="number" min="1" value={roomForm.capacity_min} onChange={e => setRoomForm(p => ({ ...p, capacity_min: Number(e.target.value) }))} /></div>
              <div><Label>Máx. personas</Label><Input type="number" min="2" value={roomForm.capacity_max} onChange={e => setRoomForm(p => ({ ...p, capacity_max: Number(e.target.value) }))} /></div>
              <div><Label>Precio por persona (MXN)</Label><Input type="number" value={roomForm.price_per_person} onChange={e => setRoomForm(p => ({ ...p, price_per_person: Number(e.target.value) }))} /></div>
              <div><Label>Consumo mínimo (MXN)</Label><Input type="number" value={roomForm.min_spend} onChange={e => setRoomForm(p => ({ ...p, min_spend: Number(e.target.value) }))} /></div>
            </div>
            <div><Label>Amenidades (separadas por coma)</Label><Input value={roomForm.amenities} onChange={e => setRoomForm(p => ({ ...p, amenities: e.target.value }))} placeholder="Proyector, Sistema de audio, Decoración incluida..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoomModal(false)}>Cancelar</Button>
            <Button onClick={saveRoom} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Guardar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva Reservación Privada</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Sala *</Label>
              <Select value={bookingForm.room_id} onValueChange={v => { const r = rooms.find(x => x.id === v); setBookingForm(p => ({ ...p, room_id: v, room_name: r?.name || '' })); }}>
                <SelectTrigger><SelectValue placeholder="Seleccionar sala..." /></SelectTrigger>
                <SelectContent>{rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name} ({r.capacity_min}–{r.capacity_max} personas)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nombre del cliente *</Label><Input value={bookingForm.client_name} onChange={e => setBookingForm(p => ({ ...p, client_name: e.target.value }))} /></div>
              <div><Label>Email</Label><Input type="email" value={bookingForm.client_email} onChange={e => setBookingForm(p => ({ ...p, client_email: e.target.value }))} /></div>
              <div><Label>Teléfono</Label><Input value={bookingForm.client_phone} onChange={e => setBookingForm(p => ({ ...p, client_phone: e.target.value }))} /></div>
              <div><Label>Fecha *</Label><Input type="date" value={bookingForm.event_date} onChange={e => setBookingForm(p => ({ ...p, event_date: e.target.value }))} /></div>
              <div><Label>Hora</Label><Input type="time" value={bookingForm.event_time} onChange={e => setBookingForm(p => ({ ...p, event_time: e.target.value }))} /></div>
              <div><Label>Personas</Label><Input type="number" min="1" value={bookingForm.guests} onChange={e => setBookingForm(p => ({ ...p, guests: Number(e.target.value) }))} /></div>
              <div><Label>Ocasión</Label>
                <Select value={bookingForm.occasion} onValueChange={v => setBookingForm(p => ({ ...p, occasion: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>{OCCASIONS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Solicitudes especiales</Label><Textarea value={bookingForm.special_requests} onChange={e => setBookingForm(p => ({ ...p, special_requests: e.target.value }))} rows={2} placeholder="Decoración, platillos especiales, alérgenos..." /></div>
              <div><Label>Presupuesto estimado (MXN)</Label><Input value={bookingForm.budget} onChange={e => setBookingForm(p => ({ ...p, budget: e.target.value }))} placeholder="$15,000..." /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookingModal(false)}>Cancelar</Button>
            <Button onClick={saveBooking} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Crear Reservación'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
